import { last } from 'lodash'
import { values } from 'mobx'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from 'react-query'

import { FolderClient } from 'api/clients/FolderClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { LocationPicker, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { moveError } from 'components/NavRightClickMenu/components/MoveError/MoveError'
import { AlmanacFileSummary, HandbookItemType } from 'types'
import { useRootStore } from 'stores/RootStore'
import { useEvaluateMove } from 'components/LocationPicker/hooks/useEvaluateMove'
import { useIsBlaze } from 'hooks/useIsBlaze'

export const MovePanel = ({ file, dismiss }: { file: AlmanacFileSummary; dismiss?: () => void }) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { handbooks, files } = useRootStore()
  const { evaluateMove } = useEvaluateMove()
  const isBlaze = useIsBlaze()

  const handleMoveSuccess = (selectedFolder: SelectedFolder | null) => {
    const handbookTreeItem = values(handbooks.items).find(
      (item) => item.type === HandbookItemType.NestedHandbook && item.data.folder?.key === file.id,
    )

    handbookTreeItem?.parentHandbook?.refresh()
    queryClient.invalidateQueries(['workspace', file.workspace.id, 'files'])

    if (selectedFolder) {
      handbooks.refresh(selectedFolder.handbook_key)
      history.push(selectedFolder.seo_string)
    } else {
      // Remove because it's going to the top-level and will no longer be a handbook item
      if (handbookTreeItem) {
        handbooks.removeItem(handbookTreeItem.id)
      }
    }

    showToastMessage(
      <span>
        <b>{file.title}</b> {selectedFolder?.title && 'moved to'}{' '}
        <b>{selectedFolder?.title || `has become a top level ${isBlaze ? 'project' : 'folder'}`}</b>!
      </span>,
      'success',
    )
  }

  const handleMove = async (payload: { selectedFolder: SelectedFolder | null; newWorkspaceId: number }) => {
    const { selectedFolder, newWorkspaceId } = payload
    const dismissLoadingToast = showToastMessage('Moving...')

    const response = await FolderClient.bulkMoveFiles({
      folderIds: [file.id],
      articleIds: [],
      fileIds: [],
      destinationFolderId: selectedFolder?.id ?? null,
      destinationWorkspaceId: newWorkspaceId,
      originalWorkspaceId: file.workspace.id,
    })
    dismissLoadingToast()

    if (response.type === 'error') {
      moveError({ status: response.status, data: response.data })
      return
    }

    files.fetch(file.id, true)
    handleMoveSuccess(selectedFolder)
  }

  const parent = last(file.ancestors)

  return (
    <LocationPicker
      fileTitle={file.title}
      initialFolder={
        parent
          ? {
              id: parent.id,
              title: parent.title,
              ancestor_keys: file.ancestors.map((f) => f.id),
            }
          : null
      }
      onSelectFolder={async (payload) => {
        dismiss?.()
        await evaluateMove({
          onSelectFolderPayload: payload,
          fileMoveAttemptPayload: {
            folderIds: [file.id],
            articleIds: [],
            originalWorkspaceId: file.workspace.id,
          },
          handleMove,
        })
      }}
      actionType="move"
      isRootDisabled={!parent}
      disabledFolderIds={[file.id]}
    />
  )
}
